import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from 'sonner';
import { auth } from '@/lib/firebase';
import { getValidToken } from '@/js/external-js/variableModules/utils/getValidToken.util';
import { Textarea } from "@/components/ui/textarea";
import { useTenancy } from '@/contexts/TenancyContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Document, DocumentStatus, DocumentType, DataClassification } from '../mockDocuments';
import { Download, Upload, FileText, X, Link, FileUp, FileQuestion, Paperclip, ChevronDown, ChevronRight, Search } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
// import { Separator } from "@/components/ui/separator";
// import { Badge } from "@/components/ui/badge";
// import { Card, CardContent } from "@/components/ui/card";
// import { renderDate } from '../../../../../shared/renderDate';
import { SingleUserCombobox } from '@/components/ui/SingleUserCombobox';
import { Info } from 'lucide-react';
import { getDocument } from '@/services/documentsService';
import { ControlCombobox, ControlsProvider } from "@/components/ui/ControlCombobox";
import { useMenuSettings } from '@/services/menusService';
import { convertStatus } from '..';
import { DatePicker } from '@/components/ui/date-picker';

// Define the structure of a document history entry based on the actual data
interface DocumentHistoryEntry {
  changedDate?: any;
  changedBy?: string;
  comments?: string;
  oldComments?: string;
  fileName?: string;
  oldFileName?: string;
  nextReviewDate?: any;
  oldNextReviewDate?: any;
  lastReviewDate?: any;
  oldLastReviewDate?: any;
  docHash?: string;
  oldDocHash?: string;
  procRef?: string[];
  procRefs?: string[];
  fieldChanges?: any[];
}

interface BulkEditSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selectedDocuments: Document[];
  onSave: (updatedDocuments: Document[], fileToUpload?: File | null) => void;
  singleDocumentMode?: boolean;
}

export const BulkEditSheet: React.FC<BulkEditSheetProps> = ({
  isOpen,
  onOpenChange,
  selectedDocuments,
  onSave,
  singleDocumentMode = false
}) => {
  const { activeTenancy, userIdToName } = useTenancy();
  // Basic fields for both modes
  const [status, setStatus] = useState<string>('no-change');
  const [type, setType] = useState<string>('no-change');
  const [owner, setOwner] = useState('');
  const [nextReviewDate, setNextReviewDate] = useState<Date | undefined>(undefined);

  const handleConfluenceImport = async (url: string) => {
    if (!url) return;

    try {
      setIsImportingConfluenceDoc(true);

      // Extract page ID from Confluence URL (format: /spaces/SPACEKEY/pages/PAGE_ID/...)
      const urlObj = new URL(url);
      const pathSegments = urlObj.pathname.split('/');
      const pagesIndex = pathSegments.indexOf('pages');
      const pageName = pathSegments[pagesIndex + 2];
      const pageId = pagesIndex !== -1 && pagesIndex + 1 < pathSegments.length
        ? pathSegments[pagesIndex + 1]
        : null;

      if (!pageId) {
        throw new Error('Could not extract page ID from Confluence URL. Make sure the URL contains "/pages/PAGE_ID/"');
      }

      // Get auth token
      const idToken = await getValidToken(auth);
      if (!idToken) {
        throw new Error('Not authenticated');
      }

      // Call Confluence Export API
      const response = await fetch(
        `${import.meta.env.VITE_CONFLUENCE_API_URL}/export/${pageId}/pdf`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'customer-id': activeTenancy.id
          }
        }
      );

      if (!response.ok) {
        const error = await response.text();
        throw new Error(`Failed to export Confluence page: ${error}`);
      }

      // Convert response to Blob and create File
      const arrayBuffer = await response.arrayBuffer();
      const pdfBlob = new Blob([arrayBuffer], { type: 'application/pdf' });
      const fileName = `${pageName || 'document'}.pdf`;
      const file = new File([pdfBlob], fileName, { type: 'application/pdf' });

      // Update the state to reflect the new file
      setFileToUpload(file);

      // If in single document mode, update the document state and trigger save
      if (singleDocumentMode && selectedDocuments.length > 0) {
        try {
          // First save the document with the new file
          const updatedDoc = {
            ...selectedDocuments[0],
            fileName: fileName,
            dateUploaded: new Date(),
            // Keep the Confluence URL for reference
            confluenceURL: url
          };

          // Save the document
          await onSave([updatedDoc], file);

          // Refresh the document data to get the latest state including any server-generated fields
          if (updatedDoc.id || updatedDoc.docID) {
            const docId = updatedDoc.id || updatedDoc.docID;
            const refreshedDoc = await getDocument(docId.toString());

            if (refreshedDoc) {
              // Update the selected documents state with the refreshed data
              selectedDocuments[0] = {
                ...selectedDocuments[0],
                ...refreshedDoc,
                fileName: fileName, // Ensure we keep the new file name
                dateUploaded: new Date(), // Format date for Firestore
                confluenceURL: url // Keep the Confluence URL
              };

              // Force a re-render with the updated document
              onSave([...selectedDocuments], file);
            }
          }

          // Show success message
          toast.success('Successfully imported and refreshed Confluence document');
        } catch (error) {
          console.error('Error saving or refreshing document:', error);
          toast.error('Document imported but there was an error refreshing the view');
        }
      } else {
        // For multiple documents or no selection, just show success
        toast.success('Successfully imported Confluence document. Click Save to apply changes.');
      }

      // Update the document source to 'file' since we now have a file
      setDocumentSource('confluence');

      // Close the sheet
      onOpenChange(false);

    } catch (error: any) {
      console.error('Error importing Confluence document:', error);
      toast.error(`Failed to import Confluence document: ${error.message}`);
    } finally {
      setIsImportingConfluenceDoc(false);
    }
  };

  // Helper function to safely convert date strings to Date objects
  const parseDate = (date: any): Date | undefined => {
    if (!date) return undefined;
    try {
      // Handle Firestore timestamps
      if (date.toDate && typeof date.toDate === 'function') {
        return date.toDate();
      }
      // Handle string dates
      if (typeof date === 'string') {
        return new Date(date);
      }
      // Handle objects with __time__ property
      if (date.__time__ && typeof date.__time__ === 'string') {
        return new Date(date.__time__);
      }
      // Default case
      return new Date(date);
    } catch (e) {
      console.error('Error parsing date:', e);
      return undefined;
    }
  };

  // Additional fields for single document mode
  const [classification, setClassification] = useState<string>('no-change');
  const [comments, setComments] = useState('');
  const [componentId, setComponentId] = useState('');
  const [confluenceURL, setConfluenceURL] = useState('');
  const [isImportingConfluenceDoc, setIsImportingConfluenceDoc] = useState(false);
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [procRefs, setProcRefs] = useState<string[]>([]);
  const [lastReviewDate, setLastReviewDate] = useState<Date | undefined>(undefined);
  const [stickyDoc, setStickyDoc] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [newFile, setNewFile] = useState<File | null>(null);
  const [documentSource, setDocumentSource] = useState<'file' | 'confluence'>('file');
  const [expandedHistoryItems, setExpandedHistoryItems] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { settings } = useMenuSettings('documentLibrary');
  const documentTypes = settings?.documentTypes || [];
  let statuses = settings?.statuses || [];
  const classifications = settings?.classifications || [];

  // Rename "expired" to "overdue"
  statuses = statuses.map((status: string) => status.toLowerCase() === 'expired' ? 'Overdue' : status);

  console.log('[DocumentLibrary@BulkEditSheet] documentTypes:', documentTypes);
  console.log('[DocumentLibrary@BulkEditSheet] statuses:', statuses);
  console.log('[DocumentLibrary@BulkEditSheet] classifications:', classifications);

  const toggleHistoryItem = (itemKey: string) => {
    setExpandedHistoryItems(prev =>
      prev.includes(itemKey)
        ? prev.filter(key => key !== itemKey)
        : [...prev, itemKey]
    );
  };

  useEffect(() => {
    if (owner) {
      console.log('Owner:', owner);
    }
  }, [owner])

  // Initialize form with document data in single document mode
  useEffect(() => {
    if (isOpen && singleDocumentMode && selectedDocuments.length > 0) {
      const doc = selectedDocuments[0];
      console.log('Loading document data:', doc);

      // Set basic fields
      setStatus(convertStatus(doc?.status) || 'Published' || 'no-change');
      setType(doc?.documentType || 'no-change');
      setOwner(doc?.uploadedBy || '');

      // setNextReviewDate(
      //   typeof doc?.nextReviewDate === 'object' && doc?.nextReviewDate !== null && '__time__' in doc?.nextReviewDate
      //     ? doc?.nextReviewDate.__time__.split('T')[0]
      //     : typeof doc?.nextReviewDate === 'string'
      //       ? doc?.nextReviewDate.split('T')[0]
      //       : ''
      // );

      // Process nextReviewDate
      setNextReviewDate(parseDate(doc?.nextReviewDate));

      // Set additional fields
      if (doc?.classification) {
        console.log('Setting classification to:', doc?.classification);
        setClassification(doc?.classification || 'no-change');
      } else {
        setClassification('no-change');
      }

      if (doc?.componentId) {
        console.log('Setting componentId to:', doc?.componentId);
        setComponentId(doc?.componentId || '');
      } else {
        setComponentId('');
      }

      setComments(doc?.comments || '');
      setConfluenceURL(doc?.confluenceURL || '');
      setProcRefs(doc?.procRef || []);
      // Process lastReviewDate
      setLastReviewDate(parseDate(doc?.lastReviewDate));
      //     ? doc?.lastReviewDate.__time__.split('T')[0]
      //     : typeof doc?.lastReviewDate === 'string'
      //       ? doc?.lastReviewDate.split('T')[0]
      //       : ''
      // );
      setStickyDoc(doc?.stickyDoc || false);

      // Determine document source based on what's present
      if (doc?.confluenceURL && doc?.confluenceURL.trim() !== '') {
        console.log('Setting document source to confluence based on confluenceURL');
        setDocumentSource('confluence');
      } else if (doc?.fileName || !doc?.confluenceURL) {
        console.log('Setting document source to file based on fileName or absence of confluenceURL');
        setDocumentSource('file');
      }
    } else if (!isOpen) {
      // Reset form when closing
      resetForm();
    }
  }, [isOpen, singleDocumentMode, selectedDocuments]);

  // Reset all form fields
  const resetForm = () => {
    // Reset basic fields
    setStatus('no-change');
    setType('no-change');
    setOwner('');
    setNextReviewDate('');

    // Reset additional fields
    setClassification('no-change');
    setComments('');
    setComponentId('');
    setConfluenceURL('');
    setProcRefs([]);
    setLastReviewDate('');
    setStickyDoc(false);
    setNewFile(null);
    setActiveTab('details');
    setDocumentSource('file');
  };

  const handleSave = () => {
    const updatedDocuments = selectedDocuments.map(doc => {
      const updatedDoc = { ...doc };

      // Update basic fields
      if (status && status !== 'no-change') updatedDoc.status = status as DocumentStatus;
      if (type && type !== 'no-change') updatedDoc.documentType = type as DocumentType;
      if (owner) {
        updatedDoc.uploadedBy = owner;
        updatedDoc.owner = owner;
      }
      if (nextReviewDate) updatedDoc.nextReviewDate = nextReviewDate;

      // Update additional fields for single document mode
      if (singleDocumentMode) {
        if (classification && classification !== 'no-change') updatedDoc.classification = classification as DataClassification;
        if (comments) updatedDoc.comments = comments;
        if (componentId) updatedDoc.componentId = componentId;
        if (confluenceURL) updatedDoc.confluenceURL = confluenceURL;
        if (procRefs.length > 0) updatedDoc.procRef = procRefs;
        if (lastReviewDate) updatedDoc.lastReviewDate = { __time__: new Date(lastReviewDate).toISOString() };
        updatedDoc.stickyDoc = stickyDoc;

        // We don't modify the document directly for file uploads
        // Instead, we'll pass the file separately
      }

      return updatedDoc;
    });

    // Pass the file for upload if we're in file mode with a new file selected
    const fileForUpload = (singleDocumentMode && newFile && documentSource === 'file')
      ? newFile
      : null;

    onSave(updatedDocuments, fileForUpload);
    onOpenChange(false);
  };

  // Get appropriate icon based on file type
  const getFileIcon = useCallback((file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (file.type.startsWith('image/')) {
      return <img src={URL.createObjectURL(file)} alt={file.name} className="w-12 h-12 object-cover rounded border" />;
    }
    if (ext === 'pdf') return <span className="flex items-center justify-center w-12 h-12 bg-red-100 text-red-600 rounded text-xs font-semibold">PDF</span>;
    if (["doc", "docx"].includes(ext || '')) return <span className="flex items-center justify-center w-12 h-12 bg-blue-100 text-blue-600 rounded text-xs font-semibold">DOC</span>;
    if (["xls", "xlsx", "csv"].includes(ext || '')) return <span className="flex items-center justify-center w-12 h-12 bg-green-100 text-green-600 rounded text-xs font-semibold">XLS</span>;
    return <span className="flex items-center justify-center w-12 h-12 bg-gray-100 text-gray-500 rounded text-xs font-semibold">FILE</span>;
  }, []);

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setNewFile(e.target.files[0]);
    }
  };

  // Trigger file input click
  const handleFileButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Remove selected file
  const handleRemoveFile = () => {
    setNewFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleReplaceDocument = () => {
    // In a real implementation, this would upload the file to a server
    console.log('Replacing document with:', newFile);
    // After successful upload, you would update the document with the new file info
  };

  const handleDownloadDocument = async () => {
    // In a real implementation, this would download the current document
    if (singleDocumentMode && selectedDocuments.length > 0) {
      const doc = selectedDocuments[0];
      if (!doc) return;

      console.log('Downloading document:', doc.fileName || doc.description);

      try {
        // Get the document ID for lookup
        const docId = doc.componentId || doc.docID || doc.id;

        if (!docId) {
          toast.error('Document ID not found');
          return;
        }

        // Get the full document instance from the service
        const docInstance = await getDocument(docId.toString());

        if (!docInstance) {
          toast.error('Document not found');
          return;
        }

        // Now we can call download on the proper document instance
        await docInstance.download();
        toast.success('Document downloaded successfully');
      } catch (error) {
        console.error('Download error:', error);

        // Get a meaningful error message
        const errorMessage = error instanceof Error
          ? error.message
          : 'Failed to download document. The file may not exist.';

        toast.error(errorMessage);
      }
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="bg-white dark:bg-secondary overflow-y-auto max-h-screen">
        <SheetHeader>
          <SheetTitle>{singleDocumentMode ? 'Edit Document' : 'Bulk Edit Documents'}</SheetTitle>
          <SheetDescription>
            {singleDocumentMode
              ? selectedDocuments.length > 0 ? `Editing: ${selectedDocuments[0].fileName || selectedDocuments[0].description}` : 'Edit document details'
              : `Edit ${selectedDocuments.length} selected document${selectedDocuments.length !== 1 ? 's' : ''}. Only filled fields will be updated.`
            }
          </SheetDescription>
        </SheetHeader>

        {singleDocumentMode ? (
          <div className="py-6">
            <Tabs defaultValue="details" value={activeTab} onValueChange={setActiveTab} className="w-full p-4">
              <TabsList className="grid grid-cols-3 mb-4 items-center w-full">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="file">File</TabsTrigger>
                <TabsTrigger value="history">History</TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="space-y-4 p-2">
                <div className="flex flex-row gap-2 items-center">
                  <Label htmlFor="status" className="w-24 flex-shrink-0">Status</Label>
                  <div className="flex-1 flex items-center h-10 px-3 py-2 text-sm border rounded-md bg-muted/50 text-muted-foreground">
                    {status === 'no-change' ? 'No change' : convertStatus(status)}
                  </div>
                </div>

                <div className="flex flex-row gap-2 items-center">
                  <Label htmlFor="type" className="w-24 flex-shrink-0">Type</Label>
                  <Select value={type} onValueChange={(value) => setType(value)} className="flex-1">
                    <SelectTrigger id="type" className="w-full">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* DED-943: Filter out 'Task Evidence' from selectable types - it's a system-managed type */}
                      {documentTypes.filter((t: string) => t.toLowerCase() !== 'task evidence').map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-row gap-2 items-center min-w-0">
                  <Label htmlFor="owner" className="w-24 flex-shrink-0">Owner</Label>
                  <div className="flex-1 min-w-0">
                    <SingleUserCombobox
                      selected={owner}
                      setSelected={setOwner}
                      placeholder="Select who uploaded the document..."
                      className="w-full"
                      onChange={(value) => setOwner(value)}
                    />
                  </div>
                </div>

                <div className="flex flex-row gap-2 items-center">
                  <Label htmlFor="classification" className="w-24 flex-shrink-0">Classification</Label>
                  <Select value={classification} onValueChange={(value) => setClassification(value)} className="flex-1">
                    <SelectTrigger id="classification" className="w-full">
                      <SelectValue placeholder="Select classification" />
                    </SelectTrigger>
                    <SelectContent>
                      {classifications.map((classification) => (
                        <SelectItem key={classification} value={classification}>
                          {classification}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-row gap-2 items-center">
                  <Label htmlFor="componentId" className="w-24 flex-shrink-0">Component ID</Label>
                  <Input
                    id="componentId"
                    placeholder="Component ID"
                    value={componentId}
                    onChange={(e) => setComponentId(e.target.value)}
                    className="flex-1 text-muted-foreground"
                    readOnly
                  />
                </div>
                <div className="flex flex-row gap-2 items-center">
                  <Label className="w-24 flex-shrink-0">Last Review</Label>
                  <div className="flex-1">
                    <DatePicker
                      date={lastReviewDate}
                      setDate={setLastReviewDate}
                      fullWidth
                      enableNaturalInput
                      showPreview
                      placeholder="Select last review date"
                    />
                  </div>
                </div>

                <div className="flex flex-row gap-2 items-center">
                  <Label className="w-24 flex-shrink-0">Next Review</Label>
                  <div className="flex-1">
                    <DatePicker
                      date={nextReviewDate}
                      setDate={setNextReviewDate}
                      fullWidth
                      enableNaturalInput
                      showPreview
                      placeholder="Select next review date"
                    />
                  </div>
                </div>

                <div className="flex flex-row gap-2">
                  <Label htmlFor="comments" className="w-24 flex-shrink-0">Comments</Label>
                  <Textarea
                    id="comments"
                    placeholder="Document comments"
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    className="flex-1 min-h-[100px]"
                  />
                </div>

                <div className="flex flex-col gap-2 py-5" >
                  <Label className="w-auto flex-shrink-0">Related Controls</Label>
                  <div className="p-2 border rounded-md">
                    <div className="max-w-md">
                      <ControlCombobox
                        selected={procRefs}
                        setSelected={setProcRefs}
                        placeholder="Search for controls..."
                        className="w-full"
                        onControlSelect={(controlValue) => {
                          if (!procRefs.includes(controlValue)) {
                            setProcRefs([...procRefs, controlValue]);
                          }
                        }}
                        onControlDeSelect={(controlValue) => {
                          setProcRefs(procRefs.filter(ref => ref !== controlValue));
                        }}
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {procRefs.length === 0 && (
                      <div className="text-sm text-muted-foreground flex items-center">
                        <Search className="h-3 w-3 mr-1" />
                        <span>No controls selected. Use the search box above to find and select controls.</span>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="file" className="space-y-4 p-2">
                {/* Current document info */}
                {selectedDocuments.length > 0 && selectedDocuments[0].fileName && (
                  <div className="flex flex-col gap-2 p-4 border rounded-md">
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-blue-500" />
                      <span className="font-medium">{selectedDocuments[0].fileName}</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {selectedDocuments[0].docHash && (
                        <p>Hash: {selectedDocuments[0].docHash}</p>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      className="mt-2 w-full"
                      onClick={handleDownloadDocument}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download Document
                    </Button>
                  </div>
                )}

                {/* Document source selection */}
                <div className="flex flex-col gap-4 p-4 border rounded-md">
                  <h4 className="font-medium">Document Source</h4>
                  <div className="flex gap-2">
                    <Button
                      variant={documentSource === 'file' ? "default" : "outline"}
                      className="flex-1"
                      onClick={() => setDocumentSource('file')}
                    >
                      <FileUp className="h-4 w-4 mr-2" />
                      File Upload
                    </Button>
                    <Button
                      variant={documentSource === 'confluence' ? "default" : "outline"}
                      className="flex-1"
                      onClick={() => setDocumentSource('confluence')}
                    >
                      <Link className="h-4 w-4 mr-2" />
                      Confluence
                    </Button>
                  </div>

                  {/* Confluence URL option */}
                  {documentSource === 'confluence' && (
                    <div className="flex flex-col gap-2 mt-2">
                      {/* Current Confluence URL display */}
                      {selectedDocuments.length > 0 && selectedDocuments[0].confluenceURL && (
                        <div className="flex flex-row gap-4 w-full items-center border rounded p-2 bg-gray-50 dark:bg-[#202020] relative group mb-2">
                          <Link className="h-5 w-5 text-blue-500 flex-shrink-0" />
                          <div className="flex flex-col">
                            <span className="text-xs font-medium">Current Confluence Document</span>
                            <a
                              href={selectedDocuments[0].confluenceURL}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-500 hover:underline truncate max-w-64"
                            >
                              {selectedDocuments[0].confluenceURL}
                            </a>
                          </div>
                        </div>
                      )}

                      {/* New Confluence URL input */}
                      <div className="flex flex-col gap-2">
                        <div>
                          <Input
                            placeholder="New Confluence URL"
                            value={confluenceURL}
                            onChange={(e) => setConfluenceURL(e.target.value)}
                            className="flex-1"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={async () => {
                            if (confluenceURL) {
                              await handleConfluenceImport(confluenceURL);
                            } else if (selectedDocuments.length > 0 && selectedDocuments[0].confluenceURL) {
                              setConfluenceURL(selectedDocuments[0].confluenceURL);
                              await handleConfluenceImport(selectedDocuments[0].confluenceURL);
                            }
                          }}
                          disabled={!confluenceURL && (!selectedDocuments.length || !selectedDocuments[0].confluenceURL) || isImportingConfluenceDoc}
                        >
                          <Link className="h-4 w-4 mr-2" />
                          {isImportingConfluenceDoc ? 'Importing...' : 'Pull'}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* File upload option */}
                  {documentSource === 'file' && (
                    <div className="flex flex-col gap-2 mt-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full flex items-center justify-center gap-2"
                        onClick={handleFileButtonClick}
                      >
                        <Paperclip className="h-4 w-4" />
                        Attach Document
                        <input
                          ref={fileInputRef}
                          type="file"
                          style={{ display: 'none' }}
                          onChange={handleFileChange}
                          accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx,.csv,.txt"
                        />
                      </Button>

                      {newFile && (
                        <div className="flex flex-wrap gap-2">
                          <div className="flex flex-row gap-4 w-full items-center border rounded p-2 bg-gray-50 dark:bg-[#202020] relative group">
                            {getFileIcon(newFile)}
                            <span className="text-xs mt-1 w-full truncate" title={newFile.name}>{newFile.name}</span>
                            <button
                              type="button"
                              className="absolute top-0 right-0 m-1 p-1 rounded-full bg-white text-gray-500 hover:text-red-600 hover:bg-red-100 shadow group-hover:block"
                              style={{ display: 'block' }}
                              title="Remove attachment"
                              onClick={handleRemoveFile}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>

                          <Button
                            variant="secondary"
                            onClick={handleReplaceDocument}
                            className="mt-2 w-full"
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            Upload New Version
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedDocuments.length > 0 && selectedDocuments[0].fileName && selectedDocuments[0].confluenceURL && (
                    <div className="text-xs text-amber-500 mt-1">
                      This document has is being synced from a Confluence URL. Please ensure you pull the latest version from Confluence if you have updated the document in Confluence.
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* History Tab */}
              <TabsContent value="history" className="space-y-4 p-2">
                <div className="border rounded-md overflow-hidden">
                  {selectedDocuments.length > 0 && selectedDocuments[0].documentHistory ? (
                    <div className="flex flex-col">
                      <div className="bg-primary/15 dark:bg-primary/20 text-primary p-3 font-medium text-sm">
                        Document History
                      </div>
                      <div className="divide-y">
                        {Object.entries(selectedDocuments[0].documentHistory)
                          .filter(([key]) => key !== 'historyCount')
                          .sort((a, b) => {
                            // Sort by date descending (most recent first)
                            const dateA = a[1] && typeof a[1] === 'object' && a[1].changedDate ? a[1].changedDate.toDate().getTime() : 0;
                            const dateB = b[1] && typeof b[1] === 'object' && b[1].changedDate ? b[1].changedDate.toDate().getTime() : 0;
                            return dateB - dateA;
                          })
                          .map(([key, entry]) => {
                            if (typeof entry === 'object' && entry !== null) {
                              // Cast entry to DocumentHistoryEntry to get proper type checking
                              const historyEntry = entry as DocumentHistoryEntry;

                              // Handle different date formats (similar to columns.tsx implementation)
                              let dateObj: Date | null = null;

                              if (historyEntry.changedDate) {
                                const changedDate = historyEntry.changedDate as any;
                                if (changedDate.toDate && typeof changedDate.toDate === 'function') {
                                  // Handle Firebase Timestamp
                                  dateObj = changedDate.toDate();
                                } else if (changedDate.__time__) {
                                  // Handle object with __time__ property
                                  dateObj = new Date(changedDate.__time__);
                                } else if (typeof changedDate === 'string') {
                                  // Handle string date
                                  dateObj = new Date(changedDate);
                                } else {
                                  // Try to use it directly
                                  try {
                                    dateObj = new Date(changedDate);
                                  } catch (e) {
                                    dateObj = null;
                                  }
                                }
                              }

                              const date = dateObj ? dateObj.toLocaleDateString() : 'Unknown date';
                              const time = dateObj ? dateObj.toLocaleTimeString() : '';

                              return (
                                <div key={key} className="p-3 hover:bg-primary/5 dark:hover:bg-primary/10">
                                  <div
                                    className="flex justify-between items-start mb-2 cursor-pointer"
                                    onClick={() => toggleHistoryItem(key)}
                                  >
                                    <div className="flex items-center">
                                      {expandedHistoryItems.includes(key) ?
                                        <ChevronDown className="h-4 w-4 mr-2" /> :
                                        <ChevronRight className="h-4 w-4 mr-2" />
                                      }
                                      <div className="font-medium">{date} {time}</div>
                                    </div>
                                    <div className="text-sm text-gray-500">{userIdToName[historyEntry.changedBy] || window.companyName || 'Unknown user'}</div>
                                  </div>

                                  {expandedHistoryItems.includes(key) && (
                                    <div className="space-y-2 text-sm mt-2 pl-6 border-l-2 border-primary/30 dark:border-primary/50">
                                      {/* Show message for empty history items with no specific changes */}
                                      {(() => {
                                        // Check if this is an empty history item (only has changedDate)
                                        const hasNoChanges = (
                                          Object.keys(historyEntry).length === 1 || // Only changedDate
                                          (Object.keys(historyEntry).length === 2 && historyEntry.changedBy) // Only changedDate and changedBy
                                        );

                                        if (hasNoChanges) {
                                          return (
                                            <div className="flex items-start gap-2">
                                              <Info className="h-4 w-4 text-primary/60 mt-0.5" />
                                              <div>
                                                <span className="text-gray-600 dark:text-gray-400 italic">Document was accessed but no specific changes were recorded</span>
                                              </div>
                                            </div>
                                          );
                                        }
                                        return null;
                                      })()}
                                      {/* Basic file information */}
                                      {historyEntry.fileName && !historyEntry.oldFileName && (
                                        <div className="flex items-start gap-2">
                                          <FileText className="h-4 w-4 text-blue-500 mt-0.5" />
                                          <div>
                                            <span className="text-gray-600 dark:text-gray-400">File: </span>
                                            <span className="font-medium">{historyEntry.fileName}</span>
                                          </div>
                                        </div>
                                      )}

                                      {/* File changes */}
                                      {historyEntry.fileName && historyEntry.oldFileName && (
                                        <div className="flex items-start gap-2">
                                          <FileText className="h-4 w-4 text-blue-500 mt-0.5" />
                                          <div>
                                            <span className="text-gray-600 dark:text-gray-400">File changed from </span>
                                            <span className="font-medium">{historyEntry.oldFileName}</span>
                                            <span className="text-gray-600 dark:text-gray-400"> to </span>
                                            <span className="font-medium">{historyEntry.fileName}</span>
                                          </div>
                                        </div>
                                      )}

                                      {/* Comments (without change) */}
                                      {historyEntry.comments && historyEntry.oldComments === undefined && (
                                        <div className="flex items-start gap-2">
                                          <FileText className="h-4 w-4 text-green-500 mt-0.5" />
                                          <div>
                                            <span className="text-gray-600 dark:text-gray-400">Comments: </span>
                                            <div className="mt-1 text-xs bg-green-50 dark:bg-green-900/20 p-2 rounded border border-green-100 dark:border-green-900/30">
                                              {historyEntry.comments}
                                            </div>
                                          </div>
                                        </div>
                                      )}

                                      {/* Comments changes */}
                                      {historyEntry.comments && historyEntry.oldComments !== undefined && (
                                        <div className="flex items-start gap-2">
                                          <FileText className="h-4 w-4 text-green-500 mt-0.5" />
                                          <div>
                                            <span className="text-gray-600 dark:text-gray-400">Comments updated</span>
                                            {historyEntry.oldComments && (
                                              <div className="mt-1 text-xs bg-blue-50 dark:bg-blue-900/20 p-2 rounded border border-blue-100 dark:border-blue-900/30">
                                                {historyEntry.oldComments}
                                              </div>
                                            )}
                                            {historyEntry.comments && (
                                              <div className="mt-1 text-xs bg-green-50 dark:bg-green-900/20 p-2 rounded border border-green-100 dark:border-green-900/30">
                                                {historyEntry.comments}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                      {/* Review date changes */}
                                      {historyEntry.nextReviewDate && historyEntry.oldNextReviewDate && (
                                        <div className="flex items-start gap-2">
                                          <FileText className="h-4 w-4 text-amber-500 mt-0.5" />
                                          <div>
                                            <span className="text-gray-600 dark:text-gray-400">Next review date changed from </span>
                                            <span className="font-medium">
                                              {(() => {
                                                const oldDate = entry.oldNextReviewDate as any;
                                                if (oldDate.toDate && typeof oldDate.toDate === 'function') {
                                                  return oldDate.toDate().toLocaleDateString();
                                                } else if (oldDate.__time__) {
                                                  return new Date(oldDate.__time__).toLocaleDateString();
                                                } else if (typeof oldDate === 'string') {
                                                  return new Date(oldDate).toLocaleDateString();
                                                } else {
                                                  try {
                                                    return new Date(oldDate).toLocaleDateString();
                                                  } catch (e) {
                                                    return 'Invalid date';
                                                  }
                                                }
                                              })()}
                                            </span>
                                            <span className="text-gray-600 dark:text-gray-400"> to </span>
                                            <span className="font-medium">
                                              {(() => {
                                                const newDate = entry.nextReviewDate as any;
                                                if (newDate.toDate && typeof newDate.toDate === 'function') {
                                                  return newDate.toDate().toLocaleDateString();
                                                } else if (newDate.__time__) {
                                                  return new Date(newDate.__time__).toLocaleDateString();
                                                } else if (typeof newDate === 'string') {
                                                  return new Date(newDate).toLocaleDateString();
                                                } else {
                                                  try {
                                                    return new Date(newDate).toLocaleDateString();
                                                  } catch (e) {
                                                    return 'Invalid date';
                                                  }
                                                }
                                              })()}
                                            </span>
                                          </div>
                                        </div>
                                      )}

                                      {/* Last review date changes */}
                                      {historyEntry.lastReviewDate && historyEntry.oldLastReviewDate && (
                                        <div className="flex items-start gap-2">
                                          <FileText className="h-4 w-4 text-purple-500 mt-0.5" />
                                          <div>
                                            <span className="text-gray-600 dark:text-gray-400">Last review date changed from </span>
                                            <span className="font-medium">
                                              {(() => {
                                                const oldDate = entry.oldLastReviewDate as any;
                                                if (oldDate.toDate && typeof oldDate.toDate === 'function') {
                                                  return oldDate.toDate().toLocaleDateString();
                                                } else if (oldDate.__time__) {
                                                  return new Date(oldDate.__time__).toLocaleDateString();
                                                } else if (typeof oldDate === 'string') {
                                                  return new Date(oldDate).toLocaleDateString();
                                                } else {
                                                  try {
                                                    return new Date(oldDate).toLocaleDateString();
                                                  } catch (e) {
                                                    return 'Invalid date';
                                                  }
                                                }
                                              })()}
                                            </span>
                                            <span className="text-gray-600 dark:text-gray-400"> to </span>
                                            <span className="font-medium">
                                              {(() => {
                                                const newDate = entry.lastReviewDate as any;
                                                if (newDate.toDate && typeof newDate.toDate === 'function') {
                                                  return newDate.toDate().toLocaleDateString();
                                                } else if (newDate.__time__) {
                                                  return new Date(newDate.__time__).toLocaleDateString();
                                                } else if (typeof newDate === 'string') {
                                                  return new Date(newDate).toLocaleDateString();
                                                } else {
                                                  try {
                                                    return new Date(newDate).toLocaleDateString();
                                                  } catch (e) {
                                                    return 'Invalid date';
                                                  }
                                                }
                                              })()}
                                            </span>
                                          </div>
                                        </div>
                                      )}

                                      {/* Document hash changes */}
                                      {historyEntry.docHash && historyEntry.oldDocHash && (
                                        <div className="flex items-start gap-2">
                                          <FileText className="h-4 w-4 text-gray-500 mt-0.5" />
                                          <div>
                                            <span className="text-gray-600 dark:text-gray-400">Document content updated</span>
                                          </div>
                                        </div>
                                      )}

                                      {/* Proc refs changes - handle both procRef and procRefs properties */}
                                      {(() => {
                                        // Get procedure references from either procRef or procRefs property
                                        let procReferences: string[] = [];

                                        if (historyEntry.procRef && Array.isArray(historyEntry.procRef)) {
                                          procReferences = historyEntry.procRef.filter(ref => ref !== null && ref !== undefined);
                                        } else if (historyEntry.procRefs && Array.isArray(historyEntry.procRefs)) {
                                          procReferences = historyEntry.procRefs.filter(ref => ref !== null && ref !== undefined);
                                        }

                                        // Only show if there are valid references
                                        if (procReferences.length > 0) {
                                          return (
                                            <div className="flex items-start gap-2">
                                              <FileText className="h-4 w-4 text-indigo-500 mt-0.5" />
                                              <div>
                                                <span className="text-gray-600 dark:text-gray-400">Procedure references: </span>
                                                <span className="font-medium">{procReferences.join(', ')}</span>
                                              </div>
                                            </div>
                                          );
                                        }
                                        return null;
                                      })()}
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            return null;
                          })}
                      </div>
                    </div>
                  ) : (
                    <div className="p-8 text-center text-gray-500">
                      <FileQuestion className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No history available for this document</p>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          <div className="py-6">
            <div className="grid gap-4 w-full p-4">
              <div className="flex flex-row gap-2 items-center">
                <Label htmlFor="status" className="w-24 flex-shrink-0">Status</Label>
                <div className="flex-1 flex items-center h-10 px-3 py-2 text-sm border rounded-md bg-muted/50 text-muted-foreground">
                  {status === 'no-change' ? 'No change' : status}
                </div>
              </div>

              <div className="flex flex-row gap-2 items-center">
                <Label htmlFor="type" className="w-24 flex-shrink-0">Type</Label>
                <Select value={type} onValueChange={(value) => setType(value)} className="flex-1">
                  <SelectTrigger id="type" className="w-full">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no-change">No change</SelectItem>
                    {documentTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-row gap-2 items-center">
                <Label htmlFor="owner" className="w-24 flex-shrink-0">Owner</Label>
                {/* <Input
                  id="owner"
                  placeholder="Uploaded By"
                  value={owner}
                  onChange={(e) => setOwner(e.target.value)}
                  className="flex-1"
                /> */}
                <SingleUserCombobox
                  selected={owner}
                  setSelected={setOwner}
                  placeholder="Select Uploaded By..."
                  className="w-full"
                  onChange={(value) => setOwner(value)}
                />
              </div>

              <div className="flex flex-row gap-2 items-center">
                <Label className="w-24 flex-shrink-0">Next Review</Label>
                <div className="flex-1">
                  <DatePicker
                    date={nextReviewDate}
                    setDate={setNextReviewDate}
                    fullWidth
                    enableNaturalInput
                    showPreview
                    placeholder="Select next review date"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <SheetFooter>
          <Button onClick={handleSave}>Save Changes</Button>
          <SheetClose asChild>
            <Button variant="outline">Cancel</Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
